// A lightweight CodeMirror language for Munos (`.munos`) source.
//
// Munos has no published Lezer grammar, and the dev IDE doesn't need a full
// parse tree — just legible highlighting. So this is a hand-written
// StreamLanguage tokenizer over the language's real vocabulary (drawn from
// docs/language.md and the deployment-scripts/ corpus): keywords, primitive
// types, the host builtins, numbers (dec/hex/bin), strings, chars, comments,
// and operators. If Munos ever ships a proper grammar, swap this for it
// behind the same `munos()` export.

import { StreamLanguage, LanguageSupport } from "@codemirror/language";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";

// Control-flow / declaration keywords.
const KEYWORDS = new Set([
  "function", "const", "let", "mut", "return", "break", "continue",
  "if", "else", "while", "for", "match", "struct", "event", "import",
  "export", "use", "pub", "on_exec", "on_frame",
  // Deployment / role structure.
  "deployment", "member", "server", "client",
]);

// Primitive value types.
const TYPES = new Set([
  "i8", "i16", "i32", "i64", "u8", "u16", "u32", "u64",
  "bool", "string", "void", "f32", "f64",
]);

// Host builtins — the sandbox surface a script calls into. Highlighted as
// functions so they stand apart from user identifiers. Kept in sync (loosely)
// with docs/language.md; an unlisted builtin just renders as a plain
// identifier, which is harmless.
const BUILTINS = new Set([
  "send", "recv", "listen", "broadcast",
  "read_u8", "read_u16", "write_u8", "write_u16", "read_rom", "write_rom",
  "draw", "draw_text", "draw_sprite", "draw_rect", "fill_rect",
  "pack", "unpack", "pack_bits", "packBits", "unpack_bits",
  "spawn_local", "despawn", "list_scripts",
  "db_get", "db_set", "db_delete", "db_keys",
  "local_rom_hash", "max_clients", "client_id", "is_server", "is_client",
  "compress", "decompress", "fetch", "play_sound",
  "battery_persist", "log", "abs", "min", "max", "clamp",
]);

const LITERALS = new Set(["true", "false", "null"]);

interface MunosState {
  inBlockComment: boolean;
}

const munosLanguage = StreamLanguage.define<MunosState>({
  name: "munos",
  startState: () => ({ inBlockComment: false }),
  token(stream, state) {
    // Block comments (support nesting-agnostic /* ... */).
    if (state.inBlockComment) {
      if (stream.match(/^.*?\*\//)) state.inBlockComment = false;
      else stream.skipToEnd();
      return "comment";
    }
    if (stream.match("/*")) {
      state.inBlockComment = true;
      return "comment";
    }
    // Line comments.
    if (stream.match("//")) {
      stream.skipToEnd();
      return "comment";
    }
    // Whitespace.
    if (stream.eatSpace()) return null;

    // Strings and chars.
    if (stream.match(/^"(?:[^"\\]|\\.)*"/)) return "string";
    if (stream.match(/^'(?:[^'\\]|\\.)'/)) return "string";

    // Numbers: hex, binary, decimal (with optional fraction).
    if (stream.match(/^0[xX][0-9a-fA-F_]+/)) return "number";
    if (stream.match(/^0[bB][01_]+/)) return "number";
    if (stream.match(/^\d[\d_]*(?:\.\d[\d_]*)?/)) return "number";

    // Identifiers / keywords.
    const id = stream.match(/^[A-Za-z_][A-Za-z0-9_]*/) as RegExpMatchArray | null;
    if (id) {
      const word = id[0];
      if (KEYWORDS.has(word)) return "keyword";
      if (TYPES.has(word)) return "type";
      if (LITERALS.has(word)) return "literal";
      if (BUILTINS.has(word)) return "builtin";
      return "variable";
    }

    // Operators / punctuation.
    if (stream.match(/^[+\-*/%=<>!&|^~?:]+/)) return "operator";
    stream.next();
    return null;
  },
  tokenTable: {
    comment: t.comment,
    string: t.string,
    number: t.number,
    keyword: t.keyword,
    type: t.typeName,
    literal: t.bool,
    builtin: t.function(t.variableName),
    variable: t.variableName,
    operator: t.operator,
  },
});

// A theme-neutral highlight palette. The editor container sets the base
// colors; these are the syntactic accents, chosen to read on the dark IDE
// surface (dev.css). Kept minimal — the point is legibility, not a full theme.
const munosHighlight = HighlightStyle.define([
  { tag: t.comment, color: "#6b7a86", fontStyle: "italic" },
  { tag: t.keyword, color: "#c792ea" },
  { tag: t.typeName, color: "#ffcb6b" },
  { tag: t.bool, color: "#f78c6c" },
  { tag: t.number, color: "#f78c6c" },
  { tag: t.string, color: "#c3e88d" },
  { tag: t.function(t.variableName), color: "#82aaff" },
  { tag: t.operator, color: "#89ddff" },
  { tag: t.variableName, color: "#d6deeb" },
]);

/** Munos language support for a CodeMirror editor: tokenizer + highlight. */
export function munos(): LanguageSupport {
  return new LanguageSupport(munosLanguage, [syntaxHighlighting(munosHighlight)]);
}
